import { toolkitUrls } from '@/util/links'

// Keys, category labels and category profile ids are a cross-SPA contract: both SPAs read one
// stored selection, so these must match toolkit-spa's catalog exactly. Both specs assert that.
export const CUSTOMIZATIONS = {
  GOALS: 'goals',
  AUTO_REVIEW: 'autoReview'
} as const

export type CustomizationKey = (typeof CUSTOMIZATIONS)[keyof typeof CUSTOMIZATIONS]

export interface DemoCategory {
  label: string
  profileId: string
}

export interface DemoLink {
  label: string
  url: string
}

export interface CustomizationCatalogEntry {
  key: CustomizationKey
  title: string
  description: string
  // Where to send a presenter who is not on a page that runs this customization, one per page.
  demoLinks: DemoLink[]
  pagePatterns: RegExp[]
  categories: DemoCategory[]
}

// Where auto-review runs in this SPA: the Present Levels wizard and its saved summaries, and
// Impact of Disability statements, generated and saved. Paths are this SPA's router paths, without
// the /toolkit/v base, matched the way toolkit-spa's GOAL_PAGES are. The shared present levels
// summary is not here: it renders no review button.
export const REVIEW_PAGES: RegExp[] = [/^\/presentlevels\/v2/, /^\/impact-of-disability/]

// Goal customizations are delivered by toolkit-spa, so that entry has no routes here: its picker
// is disabled, and the modal reports whether it is on and links to where it runs. Auto-review is
// delivered by both SPAs — goal pages there, present levels and impact of disability here — so its
// picker is live on REVIEW_PAGES and the selection is read by the content review store's
// `reviewProfileId`.
export const CUSTOMIZATION_CATALOG: CustomizationCatalogEntry[] = [
  {
    key: CUSTOMIZATIONS.GOALS,
    title: 'Goal Customizations',
    description: 'Apply a pre-configured customization to goals and anchor pages.',
    demoLinks: [{ label: 'Browse goals to run this demo', url: toolkitUrls.browse }],
    pagePatterns: [],
    categories: [
      { label: 'Week-based goal', profileId: 'hawaii' },
      { label: 'Baseline reference', profileId: 'minnesota-draft' },
      { label: 'Measurement criteria', profileId: 'california-irvine' },
      { label: 'State compliance format', profileId: 'tennessee' },
      { label: 'Separate annual and baseline', profileId: 'engineering-testing' }
    ]
  },
  {
    key: CUSTOMIZATIONS.AUTO_REVIEW,
    title: 'Auto-Review Rubric',
    description:
      "Review goals, present levels and impact statements against a district's rubric instead of Goalbook's criteria.",
    demoLinks: [
      // The v2 session directly: auto-review only exists in the v2 wizard, and the /presentlevels
      // entry lands on the legacy wizard.
      { label: 'Open the Present Levels Wizard to run this demo', url: toolkitUrls.presentlevelsWizardV2 },
      {
        label: 'Compose an Impact of Disability statement to run this demo',
        url: toolkitUrls.impactOfDisability(true)
      }
    ],
    pagePatterns: REVIEW_PAGES,
    categories: [
      { label: 'Full IEP checklist', profileId: 'georgia_dekalb' },
      { label: 'PLAAFP quality rubric', profileId: 'california-san_bernardino-draft' },
      { label: 'Regulation-cited rubric', profileId: 'maryland-stmarys' },
      { label: 'Impact and goals rubric', profileId: 'missouri_ssd' }
    ]
  }
]

export const isCustomizationOnPage = (
  customization: CustomizationCatalogEntry,
  path: string
): boolean => customization.pagePatterns.some((pattern) => pattern.test(path))
